<template>
    <div class="basicInformation">
        <!-- 动态配置信息组列表 (目前包含核心结果数据) -->
        <div class="common_group" v-for="item, index in configList" :key="item.title">
            <!-- 分组标题区域 -->
            <div class="title">
                <img src="@/assets/images/informationIcon.png">
                <span>
                    {{ item.title }}
                    <template v-if="startDate && endDate">
                        <span>(取值周期{{ startDate }}至{{ endDate }})</span>
                    </template>
                </span>
            </div>
            <!-- 只读模板内容展示 -->
            <div class="common_readonly_template">
                <el-row>
                    <!-- 遍历当前分组下的字段列表 -->
                    <el-col :span="24" v-for="l, li in item.list" :key="l.label">
                        <div>
                            <!-- 字段标签 -->
                            <div class="label">{{ l.label }}</div>
                            <!-- 字段值显示 -->
                            <div class="text">{{ basicData[l.props] }}</div>
                        </div>
                    </el-col>
                </el-row>
            </div>
        </div>

        <!-- 及时评估结果数据表格区域 -->
        <!-- <div class="common_group">
            <div class="title">
                <img src="@/assets/images/informationIcon.png">
                <span>{{ $t('activityQuality.timelyAssessmentResultData') }}</span>
            </div>
            <commonTable ref="timelyAssessmentResultDataRef" />
        </div> -->

        <!-- 个人预设目标完成情况表格区域 -->
        <div class="common_group">
            <div class="title">
                <img src="@/assets/images/informationIcon.png">
                <span>
                    {{ $t('activityQuality.personalPresetTargetCompletionSituation') }}
                    <template v-if="startDate && endDate">
                        <span>(取值周期{{ startDate }}至{{ endDate }})</span>
                    </template>
                </span>
            </div>
            <commonTable ref="personalPresetTargetCompletionSituationRef"
                :footerMethodHandler="personalFooterMethodHandler" :mergeFooterData="personalFooterMergeData"
                :handlerFooterCellClass="personalFooterCellClass" />
        </div>

        <!-- 单场活动质量评估表格区域 -->
        <div class="common_group">
            <div class="title">
                <img src="@/assets/images/informationIcon.png">
                <span>
                    {{ $t('activityQuality.singleActivityQualityAssessment') }}
                    <template v-if="startDate && endDate">
                        <span>(取值周期{{ startDate }}至{{ endDate }})</span>
                    </template>
                </span>
            </div>
            <commonTable ref="singleActivityQualityAssessmentRef" />
        </div>
    </div>
</template>

<script>
// 导入公共表格组件和表格配置工具类
import commonTable from '@/components/commonTable';
import { TableColumns, TableRow } from '@/utils/tableOptions';
import { activityQualityStatistics } from '@/views/activitieList/service'
import Big from 'big.js'

/**
 * 活动质量评估组件
 * 用于展示活动的质量评估数据，包括：
 * 1. 核心结果数据展示
 * 2. 及时评估结果数据表格
 * 3. 个人预设目标完成情况表格
 * 4. 单场活动质量评估表格
 */
export default {
    props: {
        // 编辑回显数据，用于数据编辑时的数据回填，查看页面仅作展示，不作编辑
        editEchoData: {
            type: Object,
            default: () => {
                return {};
            }
        }
    },
    components: {
        commonTable // 公共表格组件
    },
    data() {
        return {
            // 基础数据对象，存储各个字段的值
            basicData: {},
            // 当前语言设置
            lang: localStorage.getItem('lang') || 'zh-CN',
            // 配置列表，定义各个信息组的结构
            configList: [],
            // footer合并配置数据
            personalFooterMergeData: [
                {
                    row: 0,
                    col: 0,
                    rowspan: 1,
                    colspan: 2
                },
                {
                    row: 0,
                    col: 2,
                    rowspan: 1,
                    colspan: 2
                }
            ],
            completionRate: 0,
            startDate: '',
            endDate: '',
            lt: new Big(100)

        };
    },
    /**
     * 组件挂载后的初始化操作
     */
    mounted() {
        this.configList = [
            {
                // 核心结果数据分组
                title: this.$t('activityQuality.coreResultData'),
                list: [
                    { label: this.$t('activityQuality.actualAttendanceNum'), props: 'actualNum' }, // 实际参会人数
                    { label: this.$t('activityQuality.actualTargetUnitNum'), props: 'targetUnitNum' }, // 实际到会目标单位数
                    { label: this.$t('activityQuality.contactInfoCompletionRate'), props: 'contactCompleteRate' }, // 联系人信息完整率
                    { label: this.$t('activityQuality.actualNewBusinessNum'), props: 'createdBusOppNum' }, // 实际新建业务机会数
                    { label: this.$t('activityQuality.actualPushBusinessNum'), props: 'drivenBusOppNum' }, // 实际推动业务机会数
                    { label: this.$t('activityQuality.willingToExchangeCustomerUnitNum'), props: 'willExchangeNum' }, // 愿意进一步交流客户单位数
                    { label: this.$t('activityQuality.followUpUnitNum'), props: 'sevenDayUnitNum' }, // 7天跟进单位数
                    { label: this.$t('activityQuality.actualNewBusinessAmount'), props: 'createdBusOppAmount' }, // 实际新建业务机会金额
                    { label: this.$t('activityQuality.actualPushBusinessAmount'), props: 'drivenBusOppAmount' }, // 实际推动业务机会金额
                ]
            },
        ]
        try {
            this.init() // 执行初始化操作
        } catch (error) {
            console.log(error);
        }
    },
    methods: {
        /**
         * 初始化方法
         * 依次初始化三个表格组件的列配置
         */
        async init() {
            // 调用活动质量统计接口并输出结果
            const res = await activityQualityStatistics.getQualityStats({
                activityCode: this.$route.query.activityCode,
            })

            const data = res.data

            this.startDate = data.startDate
            this.endDate = data.endDate
            this.basicData = {
                ...data.coreResult,
                createdBusOppAmount:new Big(data.coreResult.createdBusOppAmount).div(10000).toFixed(2),
                drivenBusOppAmount:new Big(data.coreResult.drivenBusOppAmount).div(10000).toFixed(2),
                contactCompleteRate: data.contactCompleteRate ? (new Big(data.contactCompleteRate).times(this.lt) + '%') : '0%'

            }
            this.completionRate = data.completionSituation ? data.completionSituation.completionRate : 0
            // 初始化及时评估结果数据表格
            // this.timelyAssessmentResultDataInit(data.timelyEvaluationResults)
            // 初始化个人预设目标完成情况表格
            this.personalPresetTargetCompletionSituationInit(data.completionSituation, data.activityLevel)
            // 初始化单场活动质量评估表格
            this.singleActivityQualityAssessmentInit(data.singleEventQuality)
        },
        /**
         * 初始化及时评估结果数据表格
         * 配置表格列信息，包括实际成就情况、目标率、实际率等字段
         */
        timelyAssessmentResultDataInit(data) {
            let tableRow = new TableRow(); // 创建表格行配置对象

            // 添加"实际成就情况"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualAchievementSituation'))
                    ._data('actualAchievementSituation')
            );
            // 添加"目标率"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.targetRate'))
                    ._data('targetRate')
            );
            // 添加"实际率"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualRate'))
                    ._data('actualRate')
            );
            // 添加"是否达成"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.isAchieved'))
                    ._data('isAchieved')
            );
            // 添加"权重"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.weight'))
                    ._data('weight')
            );
            // 添加"实际值"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualValue'))
                    ._data('actualValue')
            );
            // 添加"目标值"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.targetValue'))
                    ._data('targetValue')
            );

            // 获取列配置并设置到表格组件中（禁用分页）
            let column = tableRow._cols();
            this.$refs.timelyAssessmentResultDataRef.setColumn(column, {
                page: false
            });
            // 数据渲染
            if (!data) return

            this.$refs.timelyAssessmentResultDataRef.loadStaticTable([
                // data.targetCustomerAttendanceRate
                {
                    actualAchievementSituation: this.$t('activityQuality.targetCustomerAttendanceRate'),
                    targetRate: data.targetCustomerAttendanceRate.targetRate !== 0 ? (new Big(data.targetCustomerAttendanceRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.targetCustomerAttendanceRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.targetCustomerAttendanceRate.isReachTarget,
                    weight: data.targetCustomerAttendanceRate.weight !== 0 ? (new Big(data.targetCustomerAttendanceRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.actualTargetUnitNum') + '：' + data.targetCustomerAttendanceRate.actualValue,
                    targetValue: this.$t('activityQuality.plannedInviteUnitNum') + '：' + data.targetCustomerAttendanceRate.targetValue
                },
                // data.participationRate
                {
                    actualAchievementSituation: this.$t('activityQuality.attendanceRate'),
                    targetRate: data.participationRate.targetRate !== 0 ? (new Big(data.participationRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.participationRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.participationRate.isReachTarget,
                    weight: data.participationRate.weight !== 0 ? (new Big(data.participationRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.actualAttendanceNum') + '：' + data.participationRate.actualValue,
                    targetValue: this.$t('activityQuality.plannedInviteNum') + '：' + data.participationRate.targetValue
                },
                // data.willExchangeRate
                {
                    actualAchievementSituation: this.$t('activityQuality.willingToExchangeCustomerRateUnit'),
                    targetRate: data.willExchangeRate.targetRate !== 0 ? (new Big(data.willExchangeRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.willExchangeRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.willExchangeRate.isReachTarget,
                    weight: data.willExchangeRate.weight !== 0 ? (new Big(data.willExchangeRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.willingToExchangeCustomerUnitNumx') + '：' + data.willExchangeRate.actualValue,
                    targetValue: this.$t('activityQuality.actualAttendanceUnitNum') + '：' + data.willExchangeRate.targetValue
                },
            ]);
        },
        /**
         * 初始化个人预设目标完成情况表格
         * 配置表格列信息，包括预设目标、实际完成、完成率、权重等字段
         */
        personalPresetTargetCompletionSituationInit(data, activityLevel) {
            let tableRow = new TableRow(); // 创建表格行配置对象

            // 添加"预设目标"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.presetTarget'))
                    ._data('presetTarget')
            );
            // 添加"实际完成"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualAchievement'))
                    ._data('actualAchievement')
            );
            // 添加"完成率"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.completionRate'))
                    ._data('completionRate')
            );
            // 添加"权重"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.weight'))
                    ._data('weight')
            );
            // 添加"取值周期"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.valueCycle'))
                    ._data('valueCycle')
            );

            // 获取列配置并设置到表格组件中（禁用分页，启用footer）
            let column = tableRow._cols();
            this.$refs.personalPresetTargetCompletionSituationRef.setColumn(column, {
                page: false,
                showFooter: true // 启用footer显示
            });
            // 数据渲染
            if (!data) return
            let personalTableList = [{
                presetTarget: `${this.$t('activityQuality.plannedInviteNum')}：${data.invitedPeopleNum.preTarget}`,
                actualAchievement: `${this.$t('activityQuality.actualAttendanceNum')}：${data.invitedPeopleNum.actualAchievement}`,
                completionRate: new Big(data.invitedPeopleNum.completionRate).times(this.lt) + '%',
                weight: new Big(data.invitedPeopleNum.weight).times(this.lt) + '%',
                valueCycle: '7' + this.$t('activityQuality.tableColumn.days')
            },
            {
                presetTarget: `${this.$t('activityQuality.plannedInviteUnitNum')}：${data.invitedCustomerNum.preTarget}`,
                actualAchievement: `${this.$t('activityQuality.actualTargetUnitNum')}：${data.invitedCustomerNum.actualAchievement}`,
                completionRate: new Big(data.invitedCustomerNum.completionRate).times(this.lt) + '%',
                weight: new Big(data.invitedCustomerNum.weight).times(this.lt) + '%',
                valueCycle: '7' + this.$t('activityQuality.tableColumn.days')
            },
            {
                presetTarget: `${this.$t('activityQuality.plannedNewBusinessNum')}：${data.createdBusOppNum.preTarget}`,
                actualAchievement: `${this.$t('activityQuality.actualNewBusinessNum')}：${data.createdBusOppNum.actualAchievement}`,
                completionRate: new Big(data.createdBusOppNum.completionRate).times(this.lt) + '%',
                weight: new Big(data.createdBusOppNum.weight).times(this.lt) + '%',
                valueCycle: '90' + this.$t('activityQuality.tableColumn.days')
            },
            {
                presetTarget: `${this.$t('activityQuality.plannedPushBusinessNum')}：${data.drivenBusOppNum.preTarget}`,
                actualAchievement: `${this.$t('activityQuality.actualPushBusinessNum')}：${data.drivenBusOppNum.actualAchievement}`,
                completionRate: new Big(data.drivenBusOppNum.completionRate).times(this.lt) + '%',
                weight: new Big(data.drivenBusOppNum.weight).times(this.lt) + '%',
                valueCycle: '90' + this.$t('activityQuality.tableColumn.days')
            }]
            // if (activityLevel !== 'B' && activityLevel !== 'C') {
            //     personalTableList.push({
            //         presetTarget: data.willExchangeNum.preTarget ? data.willExchangeNum.preTarget : '--',
            //         actualAchievement: `${this.$t('activityQuality.willingToExchangeCustomerUnitNum')}：${data.willExchangeNum.actualAchievement}`,
            //         completionRate: data.willExchangeNum.completionRate !== 0 ? (new Big(data.willExchangeNum.completionRate).times(this.lt) + '%') : '--',
            //         weight: '--',

            //     })
            // }
            this.$refs.personalPresetTargetCompletionSituationRef.loadStaticTable(personalTableList);

        },
        /**
         * 初始化单场活动质量评估表格
         * 配置表格列信息，与及时评估结果表格结构相同
         * 包括实际成就情况、目标率、实际率、是否达成、权重、实际值、目标值等字段
         */
        singleActivityQualityAssessmentInit(data) {
            let tableRow = new TableRow(); // 创建表格行配置对象

            // 添加"实际成就情况"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualAchievementSituation'))
                    ._data('actualAchievementSituation')
            );
            // 添加"目标率"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.targetRate'))
                    ._data('targetRate')
            );
            // 添加"实际率"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualRate'))
                    ._data('actualRate')
            );
            // 添加"是否达成"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.isAchieved'))
                    ._data('isAchieved')
            );
            // 添加"权重"列
            // tableRow.push(
            //     new TableColumns()
            //         ._title(this.$t('activityQuality.tableColumn.weight'))
            //         ._data('weight')
            // );
            // 添加"实际值"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.actualValue'))
                    ._data('actualValue')
            );
            // 添加"目标值"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.targetValue'))
                    ._data('targetValue')
            );
            // 添加"取值周期"列
            tableRow.push(
                new TableColumns()
                    ._title(this.$t('activityQuality.tableColumn.valueCycle'))
                    ._data('valueCycle')
            );
            // 获取列配置并设置到表格组件中（禁用分页）
            let column = tableRow._cols();
            this.$refs.singleActivityQualityAssessmentRef.setColumn(column, {
                page: false
            });
            // 数据渲染
            if (!data) return
            this.$refs.singleActivityQualityAssessmentRef.loadStaticTable([
                {
                    actualAchievementSituation: this.$t('activityQuality.targetCustomerAttendanceRate'),
                    targetRate: data.targetCustomerAttendanceRate.targetRate !== 0 ? (new Big(data.targetCustomerAttendanceRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.targetCustomerAttendanceRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.targetCustomerAttendanceRate.isReachTarget,
                    weight: data.targetCustomerAttendanceRate.weight !== 0 ? (new Big(data.targetCustomerAttendanceRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.actualTargetUnitNum') + '：' + data.targetCustomerAttendanceRate.actualValue,
                    targetValue: this.$t('activityQuality.plannedInviteUnitNum') + '：' + data.targetCustomerAttendanceRate.targetValue,
                    valueCycle: '7' + this.$t('activityQuality.tableColumn.days')
                },
                {
                    actualAchievementSituation: this.$t('activityQuality.attendanceRate'),
                    targetRate: data.participationRate.targetRate !== 0 ? (new Big(data.participationRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.participationRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.participationRate.isReachTarget,
                    weight: data.participationRate.weight !== 0 ? (new Big(data.participationRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.actualAttendanceNum') + '：' + data.participationRate.actualValue,
                    targetValue: this.$t('activityQuality.plannedInviteNum') + '：' + data.participationRate.targetValue,
                    valueCycle: '7' + this.$t('activityQuality.tableColumn.days')
                },
                {
                    actualAchievementSituation: this.$t('activityQuality.willingToExchangeCustomerRateUnit'),
                    targetRate: data.willExchangeRate.targetRate !== 0 ? (new Big(data.willExchangeRate.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.willExchangeRate.actualRate).times(this.lt) + '%',
                    isAchieved: data.willExchangeRate.isReachTarget,
                    weight: data.willExchangeRate.weight !== 0 ? (new Big(data.willExchangeRate.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.willingToExchangeCustomerUnitNumx') + '：' + data.willExchangeRate.actualValue,
                    targetValue: this.$t('activityQuality.actualAttendanceUnitNum') + '：' + data.willExchangeRate.targetValue,
                    valueCycle: '7' + this.$t('activityQuality.tableColumn.days')
                },
                {
                    actualAchievementSituation: this.$t('activityQuality.actualNewBusinessRateUnit'),
                    targetRate: data.createdBusOppNum.targetRate !== 0 ? (new Big(data.createdBusOppNum.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.createdBusOppNum.actualRate).times(this.lt) + '%',
                    isAchieved: data.createdBusOppNum.isReachTarget,
                    weight: data.createdBusOppNum.weight !== 0 ? (new Big(data.createdBusOppNum.weight).times(this.lt) + '%') : '--',
                    actualValue: this.$t('activityQuality.actualNewBusinessUnitNum') + '：' + data.createdBusOppNum.actualValue,
                    targetValue: this.$t('activityQuality.actualAttendanceUnitNum') + '：' + data.createdBusOppNum.targetValue,
                    valueCycle: '90' + this.$t('activityQuality.tableColumn.days')
                },
                {
                    actualAchievementSituation: this.$t('activityQuality.actualPushBusinessRateUnit'),
                    targetRate: data.drivenBusOppNum.targetRate !== 0 ? (new Big(data.drivenBusOppNum.targetRate).times(this.lt) + '%') : '--',
                    actualRate: new Big(data.drivenBusOppNum.actualRate).times(this.lt) + '%',
                    isAchieved: data.drivenBusOppNum.isReachTarget,
                    weight: data.drivenBusOppNum.weight !== 0 ? (new Big(data.drivenBusOppNum.weight).times(this.lt) + '%') : '--',

                    actualValue: this.$t('activityQuality.actualPushBusinessUnitNum') + '：' + data.drivenBusOppNum.actualValue,
                    targetValue: this.$t('activityQuality.actualAttendanceCrmCustomerNum') + '：' + data.drivenBusOppNum.targetValue,
                    valueCycle: '90' + this.$t('activityQuality.tableColumn.days')
                },
            ]);

        },
        /**
         * 个人预设目标完成情况表格的footer处理方法
         * 用于显示合计行，并合并第1、2列和第3、4列
         */
        personalFooterMethodHandler({ columns, data }) {
            // 返回footer数据，第1、2列合并显示"合计"，第3、4列合并显示"汇总"
            return [
                [
                    this.$t('activityQuality.comprehensiveCompletionRate'), // 第1列显示"合计"
                    '', // 第2列为空（会被合并）
                    `${new Big(this.completionRate).times(this.lt)}%`, // 第3列显示"汇总"
                    '' // 第4列为空（会被合并）
                ]
            ];
        },
        /**
         * 个人预设目标完成情况表格的footer单元格样式处理方法
         * 用于设置footer单元格的居中对齐样式
         * { $rowIndex, column, columnIndex, $columnIndex } 结构内容未使用
         */
        personalFooterCellClass() {
            return 'footer-cell-center';
        }

    }

}
</script>

<!-- 组件样式定义 -->
<style lang="scss" scoped>
/* 通用只读模板样式 */
.common_readonly_template {

    /* 所有div元素背景设为白色 */
    div {
        background-color: #fff;
    }

    /* 标签样式 - 左侧标签区域 */
    .label {
        width: 50% !important;
        overflow: hidden;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 6px 0 30px;
        line-height: 22px;
        color: #5B646E;
    }

    /* 文本样式 - 右侧内容区域 */
    .text {
        flex: 1;
        width: 0;
        padding: 10px;
        min-height: 54px;
        background: #fcfdfe;
        border-left: 1px solid #DEE1E7;
        line-height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
